import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { AppState } from 'src/app/state';
import { removeWhitespaces } from 'src/app/core/custom-form-validators';
import { selectSections, selectSelectedSection } from 'src/app/dashboard/state/sections/sections.selectors';
import { setSelectedLanguage } from 'src/app/dashboard/state/templates/templates.actions';
import {
  selectSelectedLanguage,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/templates/templates.selectors';
import { selectSelectedStudy } from 'src/app/dashboard/state/study/study.selectors';
import { Language } from 'src/app/models/language';
import { Section } from 'src/app/models/section';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { templatesTinymceInitConfig } from 'src/app/shared/components/tinymce-editor/tinymce-editor-config';
import { IMAGE_SIZES } from 'src/app/core/dashboard-constants';

export const SECTION_IMAGE_CAPTION_IDENTIFIER = 'section-image-caption';
export const CONTENT_TITLE_IDENTIFIER = 'content-title';
export const CONTENT_TEXT_IDENTIFIER = 'content-text';
export const SECTION_BUTTON_TEXT_IDENTIFIER = 'section-button-text';

export const SECTION_BUTTON_OPTIONS = {
  HYPERLINK: 'hyperlink',
  LINK_TO_SECTION: 'link to section',
};

@Component({
  selector: 'dashboard-description-section',
  templateUrl: './description-section.component.html',
  styleUrls: ['./description-section.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DescriptionSectionComponent),
      multi: true,
    },
  ],
})
export class DescriptionSectionComponent implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit {
  descriptionSectionForm: FormGroup;

  selectedSection: Section;
  availableSections: Section[];

  imgPath: string;
  isImageToEdit: boolean;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_TEMPLATE;
  IMAGE_SIZES = IMAGE_SIZES;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  isEdit = false;
  oldOption: string;

  SECTION_IMAGE_CAPTION_IDENTIFIER = SECTION_IMAGE_CAPTION_IDENTIFIER;
  CONTENT_TITLE_IDENTIFIER = CONTENT_TITLE_IDENTIFIER;
  CONTENT_TEXT_IDENTIFIER = CONTENT_TEXT_IDENTIFIER;
  SECTION_BUTTON_TEXT_IDENTIFIER = SECTION_BUTTON_TEXT_IDENTIFIER;

  SECTION_BUTTON_OPTIONS = SECTION_BUTTON_OPTIONS;

  tinymceInitConfig = templatesTinymceInitConfig;

  isEditorInitialized = false;

  onChange;
  onTouched;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.select(selectSelectedSection).subscribe((section) => {
        this.selectedSection = section;
        this.isEdit = !!this.selectedSection;
        if (this.selectedSection?.hyperlink) {
          this.oldOption = this.SECTION_BUTTON_OPTIONS.HYPERLINK;
        } else if (this.selectedSection?.linkToSection) {
          this.oldOption = this.SECTION_BUTTON_OPTIONS.LINK_TO_SECTION;
        }
      })
    );

    this.descriptionSectionForm = this.formBuilder.group({
      imageUrl: [''],
      translations: this.formBuilder.array([]),
      chosenOption: [this.oldOption],
      hyperlink: [''],
      linkToSection: [''],
    });

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => {
        this.selectedLanguage = language;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe(
        (study) =>
          (this.idsForUpload = {
            studyId: study?.id,
          })
      )
    );

    this.subscriptions.add(
      this.store.select(selectSections).subscribe((sections) => {
        this.availableSections = sections.slice();
        if (this.isEdit) {
          this.availableSections.splice(this.availableSections.indexOf(this.selectedSection), 1);
        }
      })
    );

    this.removeAllTranslationFields();
    if (this.isEdit) {
      if (this.selectedSection?.imageUrl) {
        this.isImageToEdit = true;
        this.imgPath = this.selectedSection?.imageUrl;
        this.imageUrl.patchValue(this.selectedSection?.imageUrl);
      }
      this.chosenOption.patchValue(this.oldOption);
      this.hyperlink.patchValue(this.selectedSection?.hyperlink);
      this.linkToSection.patchValue(this.selectedSection?.linkToSection);
      if (this.supportedLanguages) {
        this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
      }
    } else {
      this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
    }

    this.descriptionSectionForm.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });
  }

  get imageUrl(): AbstractControl {
    return this.descriptionSectionForm.get('imageUrl') as AbstractControl;
  }

  get translations(): FormArray {
    return this.descriptionSectionForm.get('translations') as FormArray;
  }

  get chosenOption(): AbstractControl {
    return this.descriptionSectionForm.get('chosenOption') as AbstractControl;
  }

  get hyperlink(): AbstractControl {
    return this.descriptionSectionForm.get('hyperlink') as AbstractControl;
  }

  get linkToSection(): AbstractControl {
    return this.descriptionSectionForm.get('linkToSection') as AbstractControl;
  }

  ngAfterViewInit() {
    this.isEditorInitialized = true;
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedSection?.translations?.filter(
      (translation) => translation.language === languageCode
    );

    const imageCaption = this.formBuilder.group({
      id: [filteredTranslations?.length && filteredTranslations[2] ? filteredTranslations[2]?.id : 0],
      language: [languageCode],
      identifier: [SECTION_IMAGE_CAPTION_IDENTIFIER],
      translation: [
        filteredTranslations?.length && filteredTranslations[2] ? filteredTranslations[2]?.translation : '',
        [Validators.maxLength(30), removeWhitespaces],
      ],
    });
    const contentTitle = this.formBuilder.group({
      id: [filteredTranslations?.length && filteredTranslations[3] ? filteredTranslations[3]?.id : 0],
      language: [languageCode],
      identifier: [CONTENT_TITLE_IDENTIFIER],
      translation: [
        filteredTranslations?.length && filteredTranslations[3] ? filteredTranslations[3]?.translation : '',
        [Validators.maxLength(30), removeWhitespaces],
      ],
    });
    const contentText = this.formBuilder.group({
      id: [filteredTranslations?.length && filteredTranslations[4] ? filteredTranslations[4]?.id : 0],
      language: [languageCode],
      identifier: [CONTENT_TEXT_IDENTIFIER],
      translation: [
        filteredTranslations?.length && filteredTranslations[4] ? filteredTranslations[4]?.translation : '',
        [Validators.maxLength(730), removeWhitespaces],
      ],
    });
    const buttonText = this.formBuilder.group({
      id: [filteredTranslations?.length && filteredTranslations[5] ? filteredTranslations[5]?.id : 0],
      language: [languageCode],
      identifier: [SECTION_BUTTON_TEXT_IDENTIFIER],
      translation: [
        filteredTranslations?.length && filteredTranslations[5] ? filteredTranslations[5]?.translation : '',
        [Validators.maxLength(30)],
      ],
    });

    this.translations.push(imageCaption);
    this.translations.push(contentTitle);
    this.translations.push(contentText);
    this.translations.push(buttonText);

    this.writeValue(this.translations);
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    if (value) {
      this.descriptionSectionForm.patchValue(value);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
